import React from "react";
import { Eye, AlertTriangle, CheckCircle2, Activity, Droplet, ShieldPlus } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import AppointmentCTA from "../components/AppointmentCTA";
import FAQSection from "../components/FAQSection";
import { images, jaundiceFaqs, clinic } from "../data/mock";

const causes = ["Viral hepatitis (A, B, C, E)", "Bile duct blockage / gallstones", "Fatty liver & cirrhosis", "Liver abscess or infection", "Excess red blood cell breakdown", "Certain medicines & toxins"];
const symptoms = ["Yellow eyes & skin", "Dark, tea-coloured urine", "Pale or clay-coloured stools", "Fatigue & weakness", "Abdominal pain & fever", "Loss of appetite & nausea"];

const Jaundice = () => {
  return (
    <div>
      <SEO path="/jaundice-treatment-varanasi" faqs={jaundiceFaqs} />
      <PageHero breadcrumb="Jaundice & Liver Infection"
        title="Jaundice & Liver Infection Treatment"
        subtitle="Fast, accurate diagnosis and treatment for jaundice, high bilirubin and liver infections in Varanasi." />

      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading center={false} eyebrow="Overview" title="Understanding Jaundice" />
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>Jaundice is the yellowing of the eyes and skin caused by a buildup of bilirubin — a yellow pigment produced when red blood cells break down. It is not a disease itself but a <strong className="text-slate-800">warning sign</strong> of an underlying liver or bile duct problem.</p>
              <p>A liver infection occurs when the liver becomes inflamed due to a virus, bacteria or parasite. Both conditions need prompt evaluation to identify the exact cause and start the right treatment before complications develop.</p>
              <p>With timely care from {clinic.doctorName}, most patients recover fully and return to normal health.</p>
            </div>
          </div>
          <div>
            <img src={images.liverAnatomy} alt="Liver and gallbladder anatomy" className="w-full rounded-3xl shadow-xl border border-slate-100" />
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeading center={false} eyebrow="Causes" title="Causes of Jaundice & Infection" />
            <div className="space-y-3">
              {causes.map((c, i) => (
                <div key={i} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-5 py-4 shadow-sm">
                  <AlertTriangle size={20} className="text-sky-500 shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{c}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading center={false} eyebrow="Signs & Symptoms" title="Symptoms to Watch" />
            <div className="space-y-3">
              {symptoms.map((s, i) => (
                <div key={i} className="flex items-center gap-3 bg-sky-50/60 border border-sky-100 rounded-xl px-5 py-4">
                  <Eye size={20} className="text-sky-500 shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="Treatment & Recovery" title="How We Treat It"
            subtitle="Treatment targets the root cause — with supportive care for a full, faster recovery." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Activity, t: "Accurate Diagnosis", d: "LFT, bilirubin, ultrasound and imaging to find the exact cause." },
              { icon: ShieldPlus, t: "Targeted Treatment", d: "Antivirals, antibiotics or bile duct clearance as needed." },
              { icon: Droplet, t: "Supportive Care", d: "Rest, hydration and a liver-friendly diet to aid recovery." },
              { icon: CheckCircle2, t: "Prevention", d: "Guidance to prevent recurrence and protect long-term liver health." },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-sky-500 text-white grid place-items-center mb-5"><c.icon size={24} /></div>
                <h3 className="font-heading font-semibold text-slate-900 mb-2">{c.t}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIAGNOSIS */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Diagnosis" title="How Jaundice & Liver Infection Are Diagnosed"
            subtitle="Finding the exact cause is essential — the right tests point directly to the right treatment." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { t: "Bilirubin & LFT", d: "Blood tests that measure bilirubin levels and liver enzymes to confirm jaundice and gauge liver stress." },
              { t: "Viral Hepatitis Markers", d: "Screening for Hepatitis A, B, C and E to check whether a viral infection is the cause." },
              { t: "Ultrasound Abdomen", d: "Imaging to look for bile duct blockage, gallstones, liver enlargement or abscess." },
              { t: "CBC & Infection Panel", d: "A complete blood count and markers to detect infection, inflammation or blood cell breakdown." },
              { t: "CT Scan / MRCP", d: "Advanced imaging to map the bile ducts and identify obstruction or complex problems." },
              { t: "Prothrombin Time (PT/INR)", d: "Assesses the liver's ability to make clotting proteins — an important marker of severity." },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
                <span className="font-heading text-sky-500 font-bold text-xl">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-heading font-semibold text-slate-900 mt-2 mb-2">{c.t}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECOVERY & PREVENTION */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="Recovery & Prevention" title="Recovering from Jaundice & Staying Protected"
            subtitle="With the right care, most patients make a full recovery — and simple habits keep the liver healthy." />
          <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-green-50 border border-green-100 rounded-2xl p-8">
              <h3 className="font-heading font-bold text-lg text-green-700 flex items-center gap-2 mb-5"><CheckCircle2 size={22} /> Tips for Faster Recovery</h3>
              <ul className="space-y-3">
                {[
                  "Get adequate rest to help the liver heal",
                  "Stay well hydrated with clean, safe water",
                  "Eat a light, balanced, liver-friendly diet",
                  "Strictly avoid alcohol and self-medication",
                  "Take all medicines exactly as prescribed",
                  "Attend every follow-up until reports normalise",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 text-sm"><CheckCircle2 size={18} className="text-green-500 mt-0.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="bg-sky-50 border border-sky-100 rounded-2xl p-8">
              <h3 className="font-heading font-bold text-lg text-sky-700 flex items-center gap-2 mb-5"><ShieldPlus size={22} /> How to Prevent It</h3>
              <ul className="space-y-3">
                {[
                  "Get vaccinated against Hepatitis A & B",
                  "Drink only clean, boiled or filtered water",
                  "Eat freshly cooked, hygienic food",
                  "Avoid unsafe injections and unscreened blood",
                  "Limit alcohol and unnecessary medicines",
                  "Screen early if you have any liver risk factors",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 text-sm"><ShieldPlus size={18} className="text-sky-500 mt-0.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-slate-600 leading-relaxed max-w-4xl mx-auto text-center mt-8">
            Most cases of jaundice and liver infection resolve completely once the underlying cause is treated. Recovery time varies from a couple of weeks to a few months depending on the cause and severity. Regular follow-up with {clinic.doctorName} ensures your bilirubin and liver function return fully to normal.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <div className="bg-sky-500 rounded-3xl p-8 sm:p-12 text-center max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">When Should You Visit a Doctor?</h2>
            <p className="text-sky-50 leading-relaxed text-lg">See a liver specialist immediately if you notice yellow eyes or skin, dark urine, pale stools, persistent abdominal pain or high fever. Early consultation prevents serious complications and speeds up recovery.</p>
          </div>
        </div>
      </section>

      <FAQSection faqs={jaundiceFaqs} title="Jaundice & Liver Infection — FAQs" />
      <AppointmentCTA title="Treat Jaundice Early — Consult Today" />
    </div>
  );
};

export default Jaundice;
